import type { Product } from '@/types'

/**
 * What to show where a price would be, when the product has none in the deal's
 * currency.
 *
 * The picker previously rendered a bare `-` here — the same dash used for a
 * missing product code and a missing description. A rep could not tell "no CAD
 * price" from "this is free" from "no price anywhere", which on a Canadian deal
 * meant a column of dashes and no explanation.
 *
 * Two states, and the distinction matters:
 *
 *   "No CAD price · set in USD, AUD"   the product IS priced — go and ask for a
 *                                      real Canadian one
 *   "No price set"                     genuinely unpriced everywhere; this line
 *                                      will quote at 0.00
 *
 * It names the currencies and NEVER the amount. Putting "US$1,500" beside an
 * empty CAD box is exactly how 54 products came to hold the same number in
 * several currencies — copied rather than converted. The rep should learn that
 * a real price exists elsewhere, not be handed a figure to paste.
 */
interface NoPriceLabelProps {
  product: Pick<Product, 'priced_currencies'>
  /** The deal's currency — the one that has no price. */
  currency: string
  className?: string
  /** Set when a form control references this text via aria-describedby. */
  id?: string
}

export function NoPriceLabel({ product, currency, className, id }: NoPriceLabelProps) {
  const elsewhere = (product.priced_currencies ?? []).filter((c) => c !== currency)

  if (elsewhere.length === 0) {
    return (
      <span id={id} className={`text-destructive text-xs ${className ?? ''}`}>
        No price set
      </span>
    )
  }

  return (
    <span id={id} className={`text-muted-foreground text-xs ${className ?? ''}`}>
      No {currency} price
      <span className="block">set in {elsewhere.join(', ')}</span>
    </span>
  )
}
